import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions";

import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridRenderCellParams,
} from "@mui/x-data-grid";

import { 
  Box, Card, Alert, Button, IconButton, 
  InputBase, Tooltip, Chip, styled,
  Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack
} from "@mui/material";

import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { getAll, getWithFilter } from "../../fetchs/get";
import { IProject } from "../../interface/project";
import { useNavigate } from "react-router-dom";
import CustomNoRowsOverlay from "../../components/CustomNoRowsOverlay";
import NavTab from "../../layout/NavTab";
import axios from "axios";
import { urlAPI } from "../../config/config";

// ─── Helpers statut projet ────────────────────────────────────────────────────

const FK_STATUT = { BROUILLON: 0, VALIDE: 1, CLOS: 2 };

const STATUT_CONFIG: Record<number, { label: string; bg: string; color: string; border: string }> = {
  [FK_STATUT.BROUILLON]: { label: "Brouillon", bg: "rgba(251,191,36,0.35)", color: "#78350f", border: "rgba(251,191,36,0.5)" },
  [FK_STATUT.VALIDE]:    { label: "Ouvert",    bg: "rgba(74,222,128,0.35)", color: "#14532d", border: "rgba(74,222,128,0.5)" },
  [FK_STATUT.CLOS]:      { label: "Clos",      bg: "rgba(148,163,184,0.3)", color: "#334155", border: "rgba(148,163,184,0.4)" },
};

const getStatutChip = (fkStatut: number) => {
  const cfg = STATUT_CONFIG[fkStatut] || { label: "Inconnu", bg: "rgba(148,163,184,0.12)", color: "#64748b", border: "rgba(148,163,184,0.25)" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        fontWeight: 600, color: cfg.color, borderRadius: "8px",
        border: `1px solid ${cfg.border}`,
        bgcolor: cfg.bg,
      }}
    />
  );
};

// ─── Helpers statut opportunité ───────────────────────────────────────────────

const OPP_STATUS_CONFIG: Record<string, { bg: string; color: string; border: string }> = {
  "Prospection":  { bg: "rgba(59,130,246,0.3)",   color: "#1e40af", border: "rgba(59,130,246,0.45)" },
  "Qualification":{ bg: "rgba(109,40,217,0.3)",   color: "#5b21b6", border: "rgba(109,40,217,0.45)" },
  "Proposition":  { bg: "rgba(253,224,71,0.35)",   color: "#854d0e", border: "rgba(253,224,71,0.5)" },
  "Négociation":  { bg: "rgba(253,186,116,0.35)",  color: "#9a3412", border: "rgba(253,186,116,0.5)" },
  "Gagné":        { bg: "rgba(74,222,128,0.35)",   color: "#14532d", border: "rgba(74,222,128,0.5)" },
  "Perdu":        { bg: "rgba(252,165,165,0.35)",  color: "#991b1b", border: "rgba(252,165,165,0.5)" },
};

const getOppChip = (label: string | null) => {
  if (!label) return null;
  const cfg = OPP_STATUS_CONFIG[label] || { bg: "rgba(148,163,184,0.12)", color: "#64748b", border: "rgba(148,163,184,0.25)" };
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 600, color: cfg.color, borderRadius: "8px",
        border: `1px solid ${cfg.border}`,
        bgcolor: cfg.bg,
      }}
    />
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const SearchWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  backgroundColor: "#fff",
  border: "1px solid #e2e8f0",
  transition: "all 0.2s ease",
  "&:hover": { borderColor: theme.palette.primary.main, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" },
  marginLeft: theme.spacing(2),
  width: "300px",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  display: "flex",
  alignItems: "center",
  color: "#94a3b8",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "#1e293b",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1.2, 1, 1.2, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    fontSize: "0.9rem",
  },
}));

// ─── Composant ────────────────────────────────────────────────────────────────

const TMA = () => {
  const [projects, setProjects] = useState<IProject[]>([]);
  const [error, setError] = useState("");
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "createdAt", sort: "desc" }]);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
  const STORAGE_KEY_STATUT = "tma_filterStatut";
  const STORAGE_KEY_OPP = "tma_filterOpp";
  const [filterStatut, setFilterStatut] = useState<number | null>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY_STATUT);
    return saved !== null ? JSON.parse(saved) : null;
  });
  const [filterOpp, setFilterOpp] = useState<string | null>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY_OPP);
    return saved !== null ? JSON.parse(saved) : null;
  });
  const [tmaToDelete, setTmaToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const token = useSelector((state: RootState) => state.user.token);

  const getAllProjects = () => {
    if (token) {
      getAll(token, "/projects/", setProjects, () => dispatch(sessionEnd()), setError);
    }
  };

  const handleDeleteTma = async () => {
  if (!tmaToDelete || !token) return;
  setDeleting(true);
  try {
    await axios.delete(`${urlAPI}/projects/${tmaToDelete.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setTmaToDelete(null);
    getAllProjects(); // rafraîchit la liste
  } catch (err) {
    setError("Erreur lors de la suppression du TMA");
  } finally {
    setDeleting(false);
  }
};

  useEffect(() => { getAllProjects(); }, []);
  useEffect(() => {
  if (filterStatut === null) {
    sessionStorage.removeItem(STORAGE_KEY_STATUT);
  } else {
    sessionStorage.setItem(STORAGE_KEY_STATUT, JSON.stringify(filterStatut));
  }
}, [filterStatut]);
useEffect(() => {
  if (filterOpp === null) {
    sessionStorage.removeItem(STORAGE_KEY_OPP);
  } else {
    sessionStorage.setItem(STORAGE_KEY_OPP, JSON.stringify(filterOpp));
  }
}, [filterOpp]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const handleOpenProject = (projectId: string) => {
    navigate(`/projet/${projectId}`);
  };

  // ─── Colonnes ───────────────────────────────────────────────────────────────

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Nom",
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Typography sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.875rem" }}>
          {params.value}
        </Typography>
      ),
    },
    { field: "client", headerName: "Client", flex: 1.5 },
    { field: "author", headerName: "Auteur", flex: 1.2 },
    {
      field: "oppStatusLabel",
      headerName: "Opportunité",
      flex: 1.2,
      renderCell: (params: GridRenderCellParams) => getOppChip(params.value),
    },
    {
      field: "lots",
      headerName: "Lots",
      width: 80,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" sx={{ fontWeight: 700, bgcolor: "#f1f5f9" }} />
      ),
    },
    {
      field: "createdAt",
      headerName: "Créer le",
      width: 130,
      type: "date",
      valueGetter: (params) => {
        if (!params.row.createdAt) return null;
        return new Date(params.row.createdAt);
      },
    },
    {
      field: "updatedAt",
      headerName: "Mis à jour le",
      width: 145,
      type: "date",
      valueGetter: (params) => {
        if (!params.row.updatedAt) return null;
        return new Date(params.row.updatedAt);
      },
    },
    {
      field: "fkStatut",
      headerName: "Statut",
      width: 130,
      renderCell: (params: GridRenderCellParams) => getStatutChip(params.value),
      valueFormatter: (params) => STATUT_CONFIG[params.value]?.label || "Inconnu",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      disableExport: true,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack direction="row" spacing={0.5}>
        <Tooltip title="Ouvrir">
          <span>
            <IconButton
              size="small"
              onClick={() => handleOpenProject(params.id as string)}
              sx={{ color: "#3b82f6" }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Supprimer">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setTmaToDelete({ id: params.id as string, name: params.row.name });
            }}
            sx={{ color: "#ef4444" }}
          >
            <DeleteTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
        );
      },
    },
  ];

  // ─── Rows ────────────────────────────────────────────────────────────────────

  const allRows = projects.map((project: IProject) => ({
    id: project._id,
    name: project.name,
    client: project.client ? (project.client as any).name : "N/C",
    author: project.author,
    lots: project.lots.length,
createdAt: project.syncMetadata?.date_c
  ? new Date(project.syncMetadata.date_c * 1000).toISOString()
  : project.createdAt,
updatedAt: project.syncMetadata?.date_u
  ? new Date(project.syncMetadata.date_u * 1000).toISOString()
  : (project.updatedAt || project.lastUpdatedDate || null),
    fkStatut: parseInt(String(project.syncMetadata?.fkStatut ?? 0)) || FK_STATUT.BROUILLON,
    oppStatusLabel: project.syncMetadata?.oppStatusLabel ?? null,
    isTma: project.isTma !== undefined ? project.isTma : !!(project.name && project.name.toLowerCase().includes("tma")),
  }));

  const tmaRows = useMemo(() => allRows.filter((r) => r.isTma), [allRows]);

  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (!r.isTma) return false;
      if (filterStatut !== null && r.fkStatut !== filterStatut) return false;
      if (filterOpp !== null && r.oppStatusLabel !== filterOpp) return false;
      return true;
    });
  }, [allRows, filterStatut, filterOpp]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <NavTab text="Liste des TMA" />

      <Card elevation={0} sx={{ mx: 3, mb: 3, pt: 4, px: 2, pb: 2, borderRadius: "16px", border: "1px solid #e2e8f0", flexGrow: 1, display: "flex", flexDirection: "column", position: "relative", overflow: "visible" }}>

        {/* Barre de recherche centrée en chevauchement */}
        <Box sx={{ position: "absolute", top: 0, left: "50%", transform: "translate(-50%, -50%)", zIndex: 10 }}>
          <SearchWrapper sx={{ boxShadow: "0 4px 16px rgba(0,0,0,0.06)", bgcolor: "rgba(255,255,255,0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", width: 360 }}>
            <SearchIconWrapper><SearchIcon fontSize="small" /></SearchIconWrapper>
            <StyledInputBase
              placeholder="Rechercher un projet..."
              onChange={(e) => token && getWithFilter(token, "/projects/filter/", e.target.value, setProjects, () => dispatch(sessionEnd()), setError)}
            />
          </SearchWrapper>
        </Box>

        {/* ─── Filtres Statut Projet ─────────────────────────────────── */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", fontSize: 10, mr: 0.5 }}>
            Statut :
          </Typography>
          {Object.entries(STATUT_CONFIG).map(([key, cfg]) => {
            const k = Number(key);
            const isActive = filterStatut === k;
            const count = tmaRows.filter(
              (r) => r.fkStatut === k && (filterOpp === null || r.oppStatusLabel === filterOpp)
            ).length;
            return (
              <Chip
                key={k}
                label={`${cfg.label} (${count})`}
                size="small"
                onClick={() => setFilterStatut(isActive ? null : k)}
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  cursor: "pointer",
                  background: isActive ? `linear-gradient(135deg, ${cfg.bg}, transparent 60%)` : "transparent",
                  color: isActive ? cfg.color : "#94a3b8",
                  border: `1px solid ${isActive ? cfg.border : "#e2e8f0"}`,
                  backdropFilter: isActive ? "blur(8px)" : "none",
                  WebkitBackdropFilter: isActive ? "blur(8px)" : "none",
                  "&:hover": { background: `linear-gradient(135deg, ${cfg.bg}, transparent 60%)`, color: cfg.color, border: `1px solid ${cfg.border}`, backdropFilter: "blur(8px)" },
                }}
              />
            );
          })}
          {filterStatut !== null && (
            <Chip label="✕" size="small" onClick={() => setFilterStatut(null)}
              sx={{ cursor: "pointer", background: "linear-gradient(135deg, rgba(252,165,165,0.2), transparent 60%)", color: "#b91c1c", fontWeight: 700, borderRadius: "8px", border: "1px solid rgba(252,165,165,0.3)" }} />
          )}
        </Stack>

        {/* ─── Filtres Opportunité ───────────────────────────────────── */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", fontSize: 10, mr: 0.5 }}>
            Opportunité :
          </Typography>
          {Object.entries(OPP_STATUS_CONFIG).map(([label, cfg]) => {
            const isActive = filterOpp === label;
            const count = tmaRows.filter(
              (r) => r.oppStatusLabel === label && (filterStatut === null || r.fkStatut === filterStatut)
            ).length;
            return (
              <Chip
                key={label}
                label={`${label} (${count})`}
                size="small"
                onClick={() => setFilterOpp(isActive ? null : label)}
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  cursor: "pointer",
                  background: isActive ? `linear-gradient(135deg, ${cfg.bg}, transparent 60%)` : "transparent",
                  color: isActive ? cfg.color : "#94a3b8",
                  border: `1px solid ${isActive ? cfg.border : "#e2e8f0"}`,
                  backdropFilter: isActive ? "blur(8px)" : "none",
                  WebkitBackdropFilter: isActive ? "blur(8px)" : "none",
                  "&:hover": { background: `linear-gradient(135deg, ${cfg.bg}, transparent 60%)`, color: cfg.color, border: `1px solid ${cfg.border}`, backdropFilter: "blur(8px)" },
                }}
              />
            );
          })}
          {filterOpp !== null && (
            <Chip label="✕" size="small" onClick={() => setFilterOpp(null)}
              sx={{ cursor: "pointer", background: "linear-gradient(135deg, rgba(252,165,165,0.2), transparent 60%)", color: "#b91c1c", fontWeight: 700, borderRadius: "8px", border: "1px solid rgba(252,165,165,0.3)" }} />
          )}
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableRowSelectionOnClick
          onRowClick={(params) => handleOpenProject(params.id as string)}
          slots={{
            toolbar: () => (
              <GridToolbarContainer sx={{ p: 1, gap: 1 }}>
                <GridToolbarColumnsButton sx={{ color: "#64748b" }} />
                <GridToolbarFilterButton sx={{ color: "#64748b" }} />
                <GridToolbarExport sx={{ color: "#64748b" }} csvOptions={{ delimiter: ';', utf8WithBom: true, fileName: 'Liste_des_projets' }} />
              </GridToolbarContainer>
            ),
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8fafc", color: "#64748b", borderRadius: "8px", fontWeight: 700 },
            "& .MuiDataGrid-cell": { borderColor: "#f1f5f9" },
            "& .MuiDataGrid-row": { cursor: "pointer" },
            "& .MuiDataGrid-row:hover": { bgcolor: "#f8fafc" },
          }}
        />
      </Card>

      <Dialog
        open={!!tmaToDelete}
        onClose={() => setTmaToDelete(null)}
        PaperProps={{
          sx: {
            bgcolor: 'rgb(255 255 255 / 51%)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }
        }}
      >
        <DialogTitle>Supprimer le TMA</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le TMA {tmaToDelete?.name} ? Cette action est irréversible et supprimera aussi les lots et la matrice associés.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTmaToDelete(null)} disabled={deleting}>
            Annuler
          </Button>
          <Button onClick={handleDeleteTma} variant="contained" color="error" disabled={deleting}>
            {deleting ? "Suppression..." : "Supprimer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TMA;